import React, { useEffect, useState } from 'react';
import {
  DataGridPro,
  GridColDef,
  GridFilterModel,
  GridOverlay,
  GridSortModel,
  getGridDateOperators,
} from '@mui/x-data-grid-pro';
import { Card, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import LinkIcon from '@mui/icons-material/Link';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';

import BaseToolbar from '../Datagrid/BaseToolbar';
import { QcIssueCategoryFilter, QcIssueStatusFilter } from './QcIssueFilters';
import {
  AdminQcIssueRow,
  QC_ISSUE_CATEGORY_LABELS,
  QC_ISSUE_STATUS_LABELS,
} from '../../interfaces/qcIssue.interface';
import { getAdminQcIssuesForDataGrid } from '../../services/qcReview';
import { parseQuestionCode } from '../Assessments/Workspace/workspaceDerivations';
import { QC_STATUS_RGB, ACCENT_BLUE } from '../Assessments/Workspace/qcTheme';
import { formatDate, normalizeFilterModel, sameModel, stringContainsOnly } from '../../utils/adminDashboardUtils';

const QuestionCode = styled('span')({ display: 'inline-flex', alignItems: 'center', gap: 4, color: ACCENT_BLUE, fontWeight: 600 });

function questionCodeLabel(row: AdminQcIssueRow): string {
  const code = parseQuestionCode(row.questionShortCode ?? undefined);
  if (code.domainCode && code.topicNumber && code.bpNumber) {
    return `${code.domainCode}-${code.topicNumber}.${code.bpNumber}`;
  }
  return row.questionShortCode ?? '';
}

function openWorkspace(row: AdminQcIssueRow) {
  window.open(
    `${window.location.origin}/assessment/${row.assessment}/questionnaire/${row.survey}?question=${row.question}`,
    '_blank',
  );
}

// Column `field` names double as the server's ordering/filter params (QCIssueAdminListAPIView + FilterSet).
const columns: GridColDef[] = [
  {
    field: 'assessment',
    headerName: 'ID',
    width: 80,
    filterOperators: stringContainsOnly,
    valueGetter: (params) => (params.row as AdminQcIssueRow).assessment,
  },
  { field: 'vendor', headerName: 'Vendor', width: 220, filterOperators: stringContainsOnly },
  {
    field: 'site_or_application',
    headerName: 'Site or Application',
    width: 220,
    filterOperators: stringContainsOnly,
    valueGetter: (params) => (params.row as AdminQcIssueRow).siteOrApplication,
  },
  {
    field: 'question',
    headerName: 'Question',
    width: 110,
    filterOperators: stringContainsOnly,
    valueGetter: (params) => questionCodeLabel(params.row as AdminQcIssueRow),
    renderCell: (params) => {
      const row = params.row as AdminQcIssueRow;
      return (
        <QuestionCode>
          {row.questionIsBestPractice && <WorkspacePremiumIcon sx={{ fontSize: 16 }} />}
          {questionCodeLabel(row)}
        </QuestionCode>
      );
    },
  },
  {
    field: 'category',
    headerName: 'Category',
    width: 160,
    valueGetter: (params) => {
      const row = params.row as AdminQcIssueRow;
      return QC_ISSUE_CATEGORY_LABELS[row.category] ?? row.category;
    },
    filterOperators: [{
      label: 'Is',
      value: 'is',
      getApplyFilterFn: () => null,
      InputComponent: QcIssueCategoryFilter,
    }],
  },
  { field: 'text', headerName: 'QC Issue', flex: 1, minWidth: 280, filterOperators: stringContainsOnly },
  {
    field: 'status',
    headerName: 'Status',
    width: 200,
    valueGetter: (params) => {
      const row = params.row as AdminQcIssueRow;
      return QC_ISSUE_STATUS_LABELS[row.status] ?? row.status;
    },
    renderCell: (params) => {
      const row = params.row as AdminQcIssueRow;
      const rgb = QC_STATUS_RGB[row.status] ?? '66,66,66';
      return (
        <Chip
          size="small"
          variant="outlined"
          label={QC_ISSUE_STATUS_LABELS[row.status] ?? row.status}
          sx={{ color: `rgb(${rgb})`, borderColor: `rgba(${rgb},0.5)`, fontWeight: 600 }}
        />
      );
    },
    filterOperators: [{
      label: 'Is',
      value: 'is',
      getApplyFilterFn: () => null,
      InputComponent: QcIssueStatusFilter,
    }],
  },
  {
    field: 'created_at',
    headerName: 'Created At',
    type: 'dateTime',
    width: 110,
    filterOperators: getGridDateOperators().filter((op) => op.value === 'is'),
    valueGetter: (params) => {
      const { createdAt } = params.row as AdminQcIssueRow;
      return createdAt ? new Date(createdAt) : null;
    },
    renderCell: (params) => formatDate((params.row as AdminQcIssueRow).createdAt),
  },
  {
    field: 'approved_at',
    headerName: 'Reviewed Date',
    type: 'dateTime',
    width: 130,
    filterOperators: getGridDateOperators().filter((op) => op.value === 'is'),
    valueGetter: (params) => {
      const { approvedAt } = params.row as AdminQcIssueRow;
      return approvedAt ? new Date(approvedAt) : null;
    },
    renderCell: (params) => formatDate((params.row as AdminQcIssueRow).approvedAt ?? undefined),
  },
  {
    field: 'round_number',
    headerName: 'Reviewed Round',
    width: 120,
    filterOperators: stringContainsOnly,
    valueGetter: (params) => (params.row as AdminQcIssueRow).roundNumber,
  },
  {
    field: 'link',
    headerName: 'Link',
    width: 70,
    sortable: false,
    filterable: false,
    disableExport: true,
    renderCell: (params) => {
      const row = params.row as AdminQcIssueRow;
      return (
        <IconButton
          size="small"
          color="primary"
          aria-label="Open in assessment workspace"
          disabled={!row.assessment || !row.survey}
          onClick={() => openWorkspace(row)}
        >
          <LinkIcon fontSize="small" />
        </IconButton>
      );
    },
  },
];

export default function QcIssuesAdminPanel() {
  const [rows, setRows] = useState<AdminQcIssueRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  // Draft feeds the grid, applied feeds the fetch — same split as AssessmentAdminPanel so half-built filter rows survive.
  const [filterModelDraft, setFilterModelDraft] = useState<GridFilterModel>({ items: [] });
  const [filterModelApplied, setFilterModelApplied] = useState<GridFilterModel>({ items: [] });

  const fetchAndSetRows = async () => {
    try {
      const data = await getAdminQcIssuesForDataGrid({ page, pageSize, sortModel, filterModel: filterModelApplied });
      setRows(data.results);
      setRowCount(data.count);
    } catch (error) {
      console.error('Failed to fetch QC issues', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchAndSetRows();
  }, [page, pageSize, sortModel, filterModelApplied]);

  return (
    <Card sx={{ padding: '2em' }}>
      <DataGridPro
        style={{ height: 640, width: '100%' }}
        rows={rows}
        columns={columns}
        columnBuffer={columns.length}
        pagination
        paginationMode="server"
        page={page}
        pageSize={pageSize}
        rowsPerPageOptions={[25, 50, 100]}
        rowCount={rowCount}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setPage(0);
        }}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={(model) => {
          setPage(0);
          setSortModel(model);
        }}
        filterMode="server"
        filterModel={filterModelDraft}
        onFilterModelChange={(model) => {
          setFilterModelDraft(model);
          const normalized = normalizeFilterModel(model);
          if (!sameModel(normalized, filterModelApplied)) {
            setPage(0);
            setFilterModelApplied(normalized);
          }
        }}
        loading={isLoading}
        disableSelectionOnClick
        components={{
          Toolbar: BaseToolbar,
          LoadingOverlay: () => (
            <GridOverlay>
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                <CircularProgress />
              </div>
            </GridOverlay>
          ),
        }}
      />
    </Card>
  );
}
